import React, { useEffect, useState, useCallback } from "react";
import { api, fmt } from "@/lib/api";
import { RegisterTable, RegisterToolbar, FilterSelect, FilterInput, StatusTag } from "@/components/RegisterTable";
import { DetailDrawer } from "@/components/DetailDrawer";

const CURRENCIES = ["CAD", "USD", "EUR", "GBP", "AUD", "JPY"];
const TYPES = [
  { value: "bank", label: "Bank" },
  { value: "credit_card", label: "Credit Card" },
  { value: "other", label: "Other" },
];

export default function Accounts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ activeView: "active", q: null });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const includeArchived = filters.activeView !== "active";
    api.listAccounts(includeArchived).then(d => {
      let filtered = d;
      if (filters.activeView === "archived") filtered = d.filter(a => !a.is_active);
      if (filters.activeView === "active") filtered = d.filter(a => a.is_active);
      if (filters.q) {
        const q = filters.q.toLowerCase();
        filtered = filtered.filter(a =>
          (a.name || "").toLowerCase().includes(q)
          || (a.account_number || "").toLowerCase().includes(q)
          || (a.gl_account_code || "").toLowerCase().includes(q)
        );
      }
      setRows(filtered);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [filters.activeView, filters.q]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    api.getAccount(selectedId).then(setDetail);
  }, [selectedId]);

  const columns = [
    { key: "name", label: "Name", width: "1.5fr",
      render: r => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    { key: "account_type", label: "Type", width: "120px",
      render: r => (r.account_type || "").replace("_", " ") },
    { key: "account_number", label: "Account #", width: "140px",
      render: r => r.account_number
        ? <span className="mono" style={{ fontSize: 12 }}>{r.account_number}</span>
        : <span className="muted">—</span> },
    { key: "gl_account_code", label: "GL Code", width: "100px",
      render: r => r.gl_account_code
        ? <span className="mono" style={{ fontSize: 12 }}>{r.gl_account_code}</span>
        : <span className="muted">—</span> },
    { key: "currency", label: "Curr.", width: "70px",
      render: r => <span className="mono" style={{ fontSize: 12 }}>{r.currency}</span> },
    { key: "materiality_threshold", label: "Materiality", width: "110px", align: "right",
      render: r => <span className="mono">${fmt.currency(r.materiality_threshold)}</span> },
    { key: "is_active", label: "Status", width: "110px",
      render: r => r.is_active
        ? <span className="tag ok" data-testid={`acct-status-${r.id}`}>active</span>
        : <span className="tag idle" data-testid={`acct-status-${r.id}`}>archived</span> },
  ];

  return (
    <div data-testid="accounts-page">
      <header style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div className="heading h1">Accounts</div>
          <div className="muted" style={{ marginTop: 4, fontSize: 14 }}>
            Reconciliation accounts. Archive removes from the operational worklist; the full activity history is preserved.
          </div>
        </div>
        <button
          className="btn primary"
          onClick={() => setCreating(true)}
          data-testid="acct-new-btn"
        >New account</button>
      </header>

      <RegisterToolbar testid="acct-toolbar">
        <FilterSelect
          label="View"
          value={filters.activeView === "active" ? null : filters.activeView}
          onChange={v => setFilters(f => ({ ...f, activeView: v || "active" }))}
          options={[
            { value: "archived", label: "Archived" },
            { value: "all", label: "All" },
          ]}
          testid="acct-filter-view"
        />
        <FilterInput
          label="Search"
          value={filters.q}
          onChange={v => setFilters(f => ({ ...f, q: v }))}
          placeholder="Name, account #, GL code"
          testid="acct-filter-search"
        />
        <div style={{ flex: 1 }} />
        <div className="muted" style={{ fontSize: 12 }} data-testid="acct-count">
          {loading ? "Loading…" : `${rows.length} account${rows.length === 1 ? "" : "s"}`}
        </div>
      </RegisterToolbar>

      <RegisterTable
        columns={columns}
        rows={rows}
        onRowClick={(r) => setSelectedId(r.id)}
        emptyText={loading ? "Loading…" : "No accounts match this view."}
        testid="acct-table"
      />

      <DetailDrawer
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        title={detail?.name || "Account"}
        subtitle={detail ? `${(detail.account_type || "").replace("_", " ")} · ${detail.currency}` : ""}
        testid="acct-detail"
      >
        {detail && (
          <AccountDetail
            account={detail}
            onChanged={(opts) => {
              if (opts && opts.deleted) {
                setSelectedId(null);
                load();
              } else {
                load();
                api.getAccount(selectedId).then(setDetail);
              }
            }}
          />
        )}
      </DetailDrawer>

      {creating && (
        <CreateAccountModal
          onClose={() => setCreating(false)}
          onCreated={(a) => { setCreating(false); load(); setSelectedId(a.id); }}
        />
      )}
    </div>
  );
}

function AccountDetail({ account, onChanged }) {
  const [form, setForm] = useState({
    name: account.name || "",
    account_number: account.account_number || "",
    gl_account_code: account.gl_account_code || "",
    currency: account.currency || "CAD",
    materiality_threshold: account.materiality_threshold ?? 100.0,
    notes: account.notes || "",
  });
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setDirty(true);
  }

  async function save() {
    setError(null); setBusy(true);
    try {
      const patch = { ...form };
      patch.materiality_threshold = parseFloat(patch.materiality_threshold) || 0;
      await api.patchAccount(account.id, patch);
      setDirty(false);
      onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  }

  async function archive() {
    if (!window.confirm(`Archive ${account.name}? It will be hidden from active lists. Audit lineage is preserved.`)) return;
    setError(null); setBusy(true);
    try {
      await api.patchAccount(account.id, { is_active: false });
      onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || "Archive failed.");
    } finally { setBusy(false); }
  }

  async function restore() {
    setError(null); setBusy(true);
    try {
      await api.patchAccount(account.id, { is_active: true });
      onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || "Restore failed.");
    } finally { setBusy(false); }
  }

  async function deleteAccount() {
    if (!window.confirm(
      `Permanently delete ${account.name}?\n\n`
      + "This is only allowed because the account has zero operational dependencies "
      + "(no periods, transactions, match groups, journal entries, or snapshots).\n\n"
      + "This action cannot be undone."
    )) return;
    setError(null); setBusy(true);
    try {
      await api.deleteAccount(account.id);
      // Force a parent reload + close drawer (handled by onChanged signaling 'deleted')
      onChanged({ deleted: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed.");
      setBusy(false);
    }
  }

  const ps = account.period_summary || {};
  const inProgress = ps.in_progress || 0;

  return (
    <div>
      <Section label="Account Properties">
        <Field label="Name">
          <input
            value={form.name}
            onChange={e => update("name", e.target.value)}
            data-testid="acct-edit-name"
            style={inputStyle}
          />
        </Field>
        <Field label="Account number">
          <input
            value={form.account_number}
            onChange={e => update("account_number", e.target.value)}
            data-testid="acct-edit-number"
            style={inputStyle}
          />
        </Field>
        <Field label="GL code">
          <input
            value={form.gl_account_code}
            onChange={e => update("gl_account_code", e.target.value)}
            data-testid="acct-edit-glcode"
            style={inputStyle}
          />
        </Field>
        <Field label="Currency">
          <select
            value={form.currency}
            onChange={e => update("currency", e.target.value)}
            data-testid="acct-edit-currency"
            style={inputStyle}
          >{CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}</select>
        </Field>
        <Field label="Materiality threshold">
          <input
            type="number" step="0.01"
            value={form.materiality_threshold}
            onChange={e => update("materiality_threshold", e.target.value)}
            data-testid="acct-edit-materiality"
            style={inputStyle}
          />
        </Field>
        <Field label="Notes">
          <textarea
            value={form.notes}
            rows={3}
            onChange={e => update("notes", e.target.value)}
            data-testid="acct-edit-notes"
            style={inputStyle}
          />
        </Field>
        {error && <div data-testid="acct-edit-error" className="muted" style={{ color: "var(--blocker)", fontSize: 12, marginTop: 6 }}>{error}</div>}
        {dirty && (
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button className="btn primary btn-sm" onClick={save} disabled={busy}
                    data-testid="acct-edit-save">{busy ? "Saving…" : "Save changes"}</button>
            <button className="btn btn-sm" onClick={() => { setForm({
                name: account.name || "",
                account_number: account.account_number || "",
                gl_account_code: account.gl_account_code || "",
                currency: account.currency || "CAD",
                materiality_threshold: account.materiality_threshold ?? 100.0,
                notes: account.notes || "",
              }); setDirty(false); setError(null); }}>Discard</button>
          </div>
        )}
      </Section>

      <Section label="Period Summary">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
          <Kpi label="Total" value={ps.total || 0} testid="acct-ps-total" />
          <Kpi label="In progress" value={ps.in_progress || 0} testid="acct-ps-inprog" />
          <Kpi label="Complete" value={ps.complete || 0} testid="acct-ps-complete" />
          <Kpi label="Locked" value={ps.locked || 0} testid="acct-ps-locked" />
        </div>
        {(account.recent_periods || []).slice(0, 6).map(p => (
          <div key={p.id} data-testid={`acct-period-${p.id}`} style={{
            display: "flex", justifyContent: "space-between",
            padding: "6px 0", borderBottom: "1px solid var(--line)", fontSize: 13,
          }}>
            <span>{p.period_label}</span>
            <StatusTag status={p.status} />
          </div>
        ))}
      </Section>

      <Section label="Dependencies">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 }}
             data-testid="acct-deps">
          <DepRow label="Periods" value={(account.dependencies || {}).periods || 0} testid="acct-dep-periods" />
          <DepRow label="Transactions" value={(account.dependencies || {}).transactions || 0} testid="acct-dep-transactions" />
          <DepRow label="MatchGroups" value={(account.dependencies || {}).match_groups || 0} testid="acct-dep-matchgroups" />
          <DepRow label="Journal Entries" value={(account.dependencies || {}).journal_entries || 0} testid="acct-dep-jes" />
          <DepRow label="Population Snapshots" value={(account.dependencies || {}).population_snapshots || 0} testid="acct-dep-popsnaps" />
          <DepRow label="Reconciliation Snapshots" value={(account.dependencies || {}).reconciliation_snapshots || 0} testid="acct-dep-reconsnaps" />
        </div>
        {!account.deletable && (
          <div className="muted" style={{ fontSize: 11, marginTop: 8, fontStyle: "italic" }}
               data-testid="acct-delete-unavailable">
            Delete unavailable — account contains historical activity.
            Use Archive to preserve audit lineage.
          </div>
        )}
      </Section>

      <Section label="Audit Trail">
        {(account.audit_trail || []).length === 0
          ? <div className="muted" style={{ fontSize: 12 }}>No audit entries.</div>
          : (account.audit_trail || []).slice(0, 12).map(a => (
            <div key={a.id} style={{
              padding: "8px 0", borderBottom: "1px solid var(--line)", fontSize: 12,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span className="mono" style={{ fontWeight: 600 }}>{a.action}</span>
                <span className="muted">{fmt.dateTime(a.created_at)}</span>
              </div>
              <div className="muted" style={{ marginTop: 2 }}>{a.details}</div>
            </div>
          ))}
      </Section>

      <Section label={account.is_active ? "Archive" : "Restore"}>
        {account.is_active ? (
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              {inProgress > 0
                ? <>Cannot archive while <b>{inProgress}</b> period(s) are in progress. Complete or resolve them first.</>
                : <>Archiving removes this account from active lists. Periods, transactions, and journal entries are preserved.</>}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                className="btn btn-sm"
                onClick={archive}
                disabled={busy || inProgress > 0}
                data-testid="acct-archive-btn"
              >{busy ? "Archiving…" : "Archive Account"}</button>
              {account.deletable && (
                <button
                  className="btn btn-sm"
                  onClick={deleteAccount}
                  disabled={busy}
                  data-testid="acct-delete-btn"
                  style={{ color: "var(--blocker)", borderColor: "var(--blocker)" }}
                  title="Permanently delete this account (zero dependencies)"
                >{busy ? "Deleting…" : "Delete Account"}</button>
              )}
            </div>
          </div>
        ) : (
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              This account is currently archived. Restoring returns it to active operational lists.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                className="btn primary btn-sm"
                onClick={restore}
                disabled={busy}
                data-testid="acct-restore-btn"
              >{busy ? "Restoring…" : "Restore Account"}</button>
              {account.deletable && (
                <button
                  className="btn btn-sm"
                  onClick={deleteAccount}
                  disabled={busy}
                  data-testid="acct-delete-btn"
                  style={{ color: "var(--blocker)", borderColor: "var(--blocker)" }}
                >{busy ? "Deleting…" : "Delete Account"}</button>
              )}
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

function DepRow({ label, value, testid }) {
  return (
    <div data-testid={testid} style={{
      display: "flex", justifyContent: "space-between",
      padding: "6px 10px", border: "1px solid var(--line)", borderRadius: 4,
      background: value > 0 ? "var(--surface-elev)" : "var(--surface)",
      fontSize: 12,
    }}>
      <span className="muted">{label}</span>
      <span className="mono" style={{ fontWeight: value > 0 ? 700 : 400 }}>{value}</span>
    </div>
  );
}

function CreateAccountModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", account_type: "bank", account_number: "",
    gl_account_code: "", currency: "CAD",
    materiality_threshold: 100.0, notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit() {
    if (!form.name.trim()) { setError("Name is required."); return; }
    setError(null); setBusy(true);
    try {
      const a = await api.createAccount({
        ...form,
        materiality_threshold: parseFloat(form.materiality_threshold) || 0,
      });
      onCreated(a);
    } catch (e) {
      setError(e?.response?.data?.detail || "Create failed.");
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="acct-create-modal-backdrop"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(20,20,20,0.32)",
        zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        data-testid="acct-create-modal"
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--surface)",
          width: "min(520px, 92vw)",
          padding: "22px 24px",
          borderRadius: 8,
          border: "1px solid var(--line)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        }}
      >
        <div className="heading h2" style={{ marginBottom: 6 }}>New Account</div>
        <div className="muted" style={{ fontSize: 13, marginBottom: 18 }}>
          Reconciliation account. Audit trail begins on creation.
        </div>

        <Field label="Name *">
          <input
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            data-testid="acct-create-name" autoFocus
            style={inputStyle}
          />
        </Field>
        <Field label="Account type">
          <select
            value={form.account_type}
            onChange={e => setForm(f => ({ ...f, account_type: e.target.value }))}
            data-testid="acct-create-type"
            style={inputStyle}
          >{TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select>
        </Field>
        <Field label="Account number">
          <input
            value={form.account_number}
            onChange={e => setForm(f => ({ ...f, account_number: e.target.value }))}
            data-testid="acct-create-number"
            style={inputStyle}
          />
        </Field>
        <Field label="GL code">
          <input
            value={form.gl_account_code}
            onChange={e => setForm(f => ({ ...f, gl_account_code: e.target.value }))}
            data-testid="acct-create-glcode"
            style={inputStyle}
          />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Currency">
            <select
              value={form.currency}
              onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
              data-testid="acct-create-currency"
              style={inputStyle}
            >{CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}</select>
          </Field>
          <Field label="Materiality">
            <input
              type="number" step="0.01"
              value={form.materiality_threshold}
              onChange={e => setForm(f => ({ ...f, materiality_threshold: e.target.value }))}
              data-testid="acct-create-materiality"
              style={inputStyle}
            />
          </Field>
        </div>

        {error && <div data-testid="acct-create-error" className="muted" style={{ color: "var(--blocker)", fontSize: 12, marginTop: 8 }}>{error}</div>}

        <div style={{ marginTop: 18, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary btn-sm" onClick={submit} disabled={busy}
                  data-testid="acct-create-submit">{busy ? "Creating…" : "Create Account"}</button>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: 6, fontSize: 13, fontFamily: "inherit",
  background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 4,
};

function Field({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, marginBottom: 10 }}>
      <span className="muted">{label}</span>
      {children}
    </label>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div className="muted" style={{
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em",
        marginBottom: 8, fontWeight: 600,
      }}>{label}</div>
      {children}
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div data-testid={testid} style={{
      padding: "8px 10px", border: "1px solid var(--line)", borderRadius: 6,
      background: "var(--surface-elev)",
    }}>
      <div className="muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div className="mono" style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  );
}
